import { useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Wifi, WifiOff, TrendingUp, TrendingDown, BarChart3, Trash2 } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useJobStore } from '@/store/jobStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { useQuery } from '@tanstack/react-query'
import { jobsApi } from '@/services/api/jobs'
import { backtestApi, type BacktestResult, type Trade } from '@/services/api/backtest'
import { formatDistanceToNow } from 'date-fns'
import { useJobWebSocket } from '@/hooks/useJobWebSocket'
import { LoadingPage } from '@/components/ui/loading-spinner'
import { useTranslation } from 'react-i18next'
import { showErrorToast, showSuccessToast } from '@/utils/error-handler'

export default function JobDetailPage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { loadJob, removeJob } = useJobStore()

  // Connect WebSocket for real-time updates
  const { isConnected } = useJobWebSocket(id)

  const { data: job, refetch, isLoading } = useQuery({
    queryKey: ['jobs', id],
    queryFn: () => jobsApi.get(id!),
    enabled: !!id,
  })

  // Fetch backtest results if job is a completed backtest
  const { data: backtestResult, isLoading: isLoadingBacktest, error: backtestError } = useQuery<BacktestResult>({
    queryKey: ['backtest', id],
    queryFn: () => backtestApi.get(id!),
    enabled: !!id && job?.jobType === 'backtest' && job?.status === 'completed',
    retry: false,
    refetchInterval: false, // Disable polling - WebSocket handles updates
  })
  
  // Handle backtest errors
  useEffect(() => {
    if (backtestError) {
      showErrorToast(backtestError as Error, t('errors.loadJob'))
    }
  }, [backtestError, t])

  // Track previous status to detect completion
  const previousStatusRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (job && id) {
      loadJob(id)
    }
  }, [job, id, loadJob])

  // Detect when job completes and show success toast
  useEffect(() => {
    if (!job) return

    const previousStatus = previousStatusRef.current
    const currentStatus = job.status

    // Update ref for next comparison
    previousStatusRef.current = currentStatus

    // Show success toast when job transitions to completed
    if (
      previousStatus &&
      (previousStatus === 'queued' || previousStatus === 'processing') &&
      currentStatus === 'completed'
    ) {
      showSuccessToast(t('jobs.completedSuccess'))
      // Refetch to get latest data including resultId
      refetch()
    }

    // Show error toast when job fails
    if (
      previousStatus &&
      (previousStatus === 'queued' || previousStatus === 'processing') &&
      currentStatus === 'failed'
    ) {
      showErrorToast(
        new Error(job.error || t('jobs.completedWithError')),
        t('jobs.completedWithError')
      )
    }
  }, [job?.status, job?.error, t, refetch])

  if (isLoading) {
    return <LoadingPage message={t('common.loading')} />
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">{t('errors.jobNotFound')}</p>
        <Button onClick={() => navigate('/jobs')} className="mt-4">
          {t('common.back')}
        </Button>
      </div>
    )
  }

  const getStatusBadgeVariant = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive'> = {
      completed: 'default',
      queued: 'secondary',
      processing: 'secondary',
      failed: 'destructive',
      cancelled: 'destructive',
      expired: 'destructive',
    }
    return variants[status] || 'secondary'
  }

  const formatDuration = (ms: number | null | undefined): string => {
    if (ms === null || ms === undefined) return t('common.noData')
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    if (ms < 3600000) {
      const minutes = Math.floor(ms / 60000)
      const seconds = Math.floor((ms % 60000) / 1000)
      return `${minutes}m ${seconds}s`
    }
    const hours = Math.floor(ms / 3600000)
    const minutes = Math.floor((ms % 3600000) / 60000)
    return `${hours}h ${minutes}m`
  }

  const handleDelete = async () => {
    if (!id) return
    if (confirm('Are you sure you want to delete this job? It will be permanently removed after 24 hours.')) {
      try {
        await jobsApi.delete(id)
        removeJob(id)
        showSuccessToast('Job deleted successfully')
        navigate('/jobs')
      } catch (error) {
        showErrorToast(error, 'Failed to delete job')
      }
    }
  }

  const canDelete = job?.status === 'completed' || job?.status === 'failed' || job?.status === 'expired' || job?.status === 'cancelled'

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{t('jobs.jobDetails')}</h1>
            <p className="text-muted-foreground mt-1">
              {t(`jobs.types.${job.jobType}`)} - {t(`jobs.status.${job.status}`)}
              {job.script && (
                <span className="ml-2">• Script: <span className="font-medium">{job.script.name}</span></span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? (
            <Badge variant="default" className="gap-1">
              <Wifi className="w-3 h-3" />
              {t('jobs.live')}
            </Badge>
          ) : (
            <Badge variant="secondary" className="gap-1">
              <WifiOff className="w-3 h-3" />
              {t('jobs.polling')}
            </Badge>
          )}
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh')}
          </Button>
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('common.status')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('common.status')}</span>
              <Badge variant={getStatusBadgeVariant(job.status)}>
                {t(`jobs.status.${job.status}`)}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('jobs.type')}</span>
              <span className="text-sm font-medium">{t(`jobs.types.${job.jobType}`)}</span>
            </div>
            {job.script && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Script</span>
                <span className="text-sm font-medium">{job.script.name}</span>
              </div>
            )}
            {job.dataset && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Dataset</span>
                <span className="text-sm font-medium">{job.dataset.name}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('common.created')}</span>
              <span className="text-sm">
                {job.createdAt && !isNaN(new Date(job.createdAt).getTime())
                  ? formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })
                  : t('common.noData')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('common.updated')}</span>
              <span className="text-sm">
                {job.updatedAt && !isNaN(new Date(job.updatedAt).getTime())
                  ? formatDistanceToNow(new Date(job.updatedAt), { addSuffix: true })
                  : t('common.noData')}
              </span>
            </div>
            {job.durationMs !== null && job.durationMs !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('jobs.duration') || 'Duration'}</span>
                <span className="text-sm font-medium">{formatDuration(job.durationMs)}</span>
              </div>
            )}
            {(job.progress !== undefined || job.config?.progress !== undefined) && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t('jobs.progress')}</span>
                  <span>{job.progress ?? job.config?.progress ?? 0}%</span>
                </div>
                <Progress value={job.progress ?? job.config?.progress ?? 0} />
                {(job.progressMessage ?? job.config?.progressMessage) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {job.progressMessage ?? job.config?.progressMessage}
                  </p>
                )}
                {job.config?.stage && (
                  <p className="text-xs text-muted-foreground">
                    Stage: <span className="font-medium">{job.config.stage}</span>
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('jobs.configuration')}</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded-md overflow-auto max-h-96">
              {JSON.stringify(job.config, null, 2)}
            </pre>
          </CardContent>
        </Card>

        {job.config?.progressHistory && job.config.progressHistory.length > 0 && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Progress History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-auto">
                {job.config.progressHistory.slice().reverse().map((entry, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 p-2 rounded border text-sm"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{entry.progress}%</span>
                        <Badge variant="outline" className="text-xs">
                          {entry.status}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {entry.message}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {job.error && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-destructive">{t('jobs.error')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded text-sm">
                {job.error}
              </div>
            </CardContent>
          </Card>
        )}

        {job.resultId && job.status === 'completed' && job.jobType !== 'backtest' && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>{t('jobs.result')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate(`/results/${job.resultId}`)}>
                {t('jobs.viewResults')} →
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Backtest Results */}
        {job.jobType === 'backtest' && job.status === 'completed' && (
          <>
            {isLoadingBacktest ? (
              <Card className="lg:col-span-2">
                <CardContent className="py-8">
                  <div className="text-center text-muted-foreground">Loading backtest results...</div>
                </CardContent>
              </Card>
            ) : backtestResult ? (
              <>
                {/* Performance Metrics */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5" />
                      Performance Metrics
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Initial Capital</p>
                        <p className="text-2xl font-bold">${backtestResult.metrics.initialCapital.toLocaleString()}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Final Capital</p>
                        <p className={`text-2xl font-bold ${backtestResult.metrics.finalCapital >= backtestResult.metrics.initialCapital ? 'text-green-600' : 'text-red-600'}`}>
                          ${backtestResult.metrics.finalCapital.toLocaleString()}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Total Return</p>
                        <p className={`text-2xl font-bold flex items-center gap-1 ${backtestResult.metrics.totalReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {backtestResult.metrics.totalReturn >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                          {backtestResult.metrics.totalReturn.toFixed(2)}%
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Total Trades</p>
                        <p className="text-2xl font-bold">{backtestResult.metrics.totalTrades}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Trading Statistics */}
                <Card>
                  <CardHeader>
                    <CardTitle>Trading Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Winning Trades</span>
                      <span className="font-medium text-green-600">{backtestResult.metrics.winningTrades}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Losing Trades</span>
                      <span className="font-medium text-red-600">{backtestResult.metrics.losingTrades}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Win Rate</span>
                      <span className="font-medium">{(typeof backtestResult.metrics.winRate === 'number' ? Math.min(100, backtestResult.metrics.winRate <= 1 ? backtestResult.metrics.winRate * 100 : backtestResult.metrics.winRate) : 0).toFixed(2)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Profit Factor</span>
                      <span className="font-medium">{backtestResult.metrics.profitFactor.toFixed(2)}</span>
                    </div>
                  </CardContent>
                </Card>

                {/* Risk Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle>Risk Metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Max Drawdown</span>
                      <span className="font-medium text-red-600">{backtestResult.metrics.maxDrawdown.toFixed(2)}%</span>
                    </div>
                    {backtestResult.metrics.sharpeRatio !== null && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Sharpe Ratio</span>
                        <span className="font-medium">{backtestResult.metrics.sharpeRatio.toFixed(2)}</span>
                      </div>
                    )}
                    {backtestResult.metrics.sortinoRatio !== null && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Sortino Ratio</span>
                        <span className="font-medium">{backtestResult.metrics.sortinoRatio.toFixed(2)}</span>
                      </div>
                    )}
                    {backtestResult.metrics.calmarRatio !== null && (
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Calmar Ratio</span>
                        <span className="font-medium">{backtestResult.metrics.calmarRatio.toFixed(2)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Trades Table */}
                {backtestResult.trades && backtestResult.trades.length > 0 && (
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle>Trades ({backtestResult.trades.length})</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-auto max-h-96 border rounded-md">
                        <table className="w-full text-sm">
                          <thead className="bg-muted">
                            <tr>
                              <th className="px-4 py-2 text-left font-medium">Symbol</th>
                              <th className="px-4 py-2 text-left font-medium">Signal</th>
                              <th className="px-4 py-2 text-left font-medium">Direction</th>
                              <th className="px-4 py-2 text-left font-medium">Size</th>
                              <th className="px-4 py-2 text-left font-medium">Entry</th>
                              <th className="px-4 py-2 text-left font-medium">Exit</th>
                              <th className="px-4 py-2 text-left font-medium">Entry Price</th>
                              <th className="px-4 py-2 text-left font-medium">Exit Price</th>
                              <th className="px-4 py-2 text-left font-medium">P&L</th>
                              <th className="px-4 py-2 text-left font-medium">P&L %</th>
                              <th className="px-4 py-2 text-left font-medium">Exit Reason</th>
                              <th className="px-4 py-2 text-left font-medium">Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {backtestResult.trades.slice(0, 50).map((trade: Trade) => (
                              <tr key={trade.id} className="border-t hover:bg-muted/50">
                                <td className="px-4 py-2 font-medium">
                                  {trade.symbol || '-'}
                                </td>
                                <td className="px-4 py-2">
                                  <span className="text-xs font-medium text-muted-foreground">
                                    {trade.signalName || '-'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <Badge variant={trade.direction === 'long' ? 'default' : 'secondary'}>
                                    {trade.direction.toUpperCase()}
                                  </Badge>
                                </td>
                                <td className="px-4 py-2 font-medium">
                                  {trade.size || 1}
                                </td>
                                <td className="px-4 py-2 text-xs">
                                  {new Date(trade.entryTimestamp * 1000).toLocaleString()}
                                </td>
                                <td className="px-4 py-2 text-xs">
                                  {new Date(trade.exitTimestamp * 1000).toLocaleString()}
                                </td>
                                <td className="px-4 py-2">${trade.entryPrice.toFixed(2)}</td>
                                <td className="px-4 py-2">${trade.exitPrice.toFixed(2)}</td>
                                <td className={`px-4 py-2 font-medium ${trade.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {trade.pnl >= 0 ? '+' : ''}${trade.pnl.toFixed(2)}
                                </td>
                                <td className={`px-4 py-2 font-medium ${trade.pnlPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {trade.pnlPercent >= 0 ? '+' : ''}{trade.pnlPercent.toFixed(2)}%
                                </td>
                                <td className="px-4 py-2">
                                  <Badge variant="outline" className="text-xs">
                                    {trade.exitReason}
                                  </Badge>
                                </td>
                                <td className="px-4 py-2 text-xs">
                                  {(trade.duration / 3600).toFixed(1)}h
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {backtestResult.trades.length > 50 && (
                          <p className="text-sm text-muted-foreground mt-2 text-center">
                            Showing first 50 of {backtestResult.trades.length} trades
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Equity Curve */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Equity Curve</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {backtestResult.equityCurve && backtestResult.equityCurve.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={backtestResult.equityCurve.map(point => ({
                            timestamp: new Date(point.timestamp * 1000).toLocaleDateString(),
                            equity: point.equity
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                              dataKey="timestamp"
                              tick={{ fontSize: 12 }}
                              angle={-45}
                              textAnchor="end"
                              height={60}
                            />
                            <YAxis
                              tick={{ fontSize: 12 }}
                              domain={['dataMin', 'dataMax']}
                            />
                            <Tooltip
                              formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Equity']}
                              labelFormatter={(label) => `Date: ${label}`}
                            />
                            <Line
                              type="monotone"
                              dataKey="equity"
                              stroke="#0088FE"
                              strokeWidth={2}
                              dot={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>No equity curve data available</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Diagnostics */}
                {backtestResult.diagnostics && (
                  <Card className="lg:col-span-2">
                    <CardHeader>
                      <CardTitle>Diagnostics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        {backtestResult.diagnostics.signalsGenerated !== undefined && (
                          <div>
                            <p className="text-muted-foreground">Signals Generated</p>
                            <p className="font-medium">{backtestResult.diagnostics.signalsGenerated}</p>
                          </div>
                        )}
                        {backtestResult.diagnostics.candlesProcessed !== undefined && (
                          <div>
                            <p className="text-muted-foreground">Candles Processed</p>
                            <p className="font-medium">{backtestResult.diagnostics.candlesProcessed.toLocaleString()}</p>
                          </div>
                        )}
                        {backtestResult.diagnostics.candlesWithSignals !== undefined && (
                          <div>
                            <p className="text-muted-foreground">Candles with Signals</p>
                            <p className="font-medium">{backtestResult.diagnostics.candlesWithSignals}</p>
                          </div>
                        )}
                        {backtestResult.diagnostics.candlesSkipped !== undefined && (
                          <div>
                            <p className="text-muted-foreground">Candles Skipped</p>
                            <p className="font-medium">{backtestResult.diagnostics.candlesSkipped.toLocaleString()}</p>
                          </div>
                        )}
                        {backtestResult.diagnostics.configuredSignals && (
                          <div>
                            <p className="text-muted-foreground">Configured Signals</p>
                            <p className="font-medium">
                              Long: {backtestResult.diagnostics.configuredSignals.long?.length || 0}, 
                              Short: {backtestResult.diagnostics.configuredSignals.short?.length || 0}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="lg:col-span-2">
                <CardContent className="py-8">
                  <div className="text-center text-muted-foreground">Backtest results not available</div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  )
}
